import { wgResource, wgResourceType } from "./wgResource.js";
import { wgTexture } from "./wgTexture.js";

class wgFbo extends wgResource {
    constructor(name, context) {
        super(name, context, wgResourceType.wgFbo);
        this.fboWidth_ = -1;
        this.fboHeight_ = -1;
        this.attachedTextures_ = [];
        this.hasDepthStencil_ = false;
        this.hasFixedSize_ = false;// for shadow mapping we dont want to resize the fbo
        this.fboHandle_ = this.getContext().gl().createFramebuffer();
        this.renderBufferHandle_ = null;
    }

    attachTexture(texture) {
        const gl = this.getContext().gl();
        // the first attachment sets the width and height
        if (this.attachedTextures_.length === 0) {
            this.fboWidth_ = texture.getWidth();
            this.fboHeight_ = texture.getHeight();
        }
        else {
            // test if there is a size match
            if (texture.getWidth() != this.fboWidth_) {
                console.log("FBO texture size mismatch: fbo width:" + this.fboWidth_ + " : texture width: " + texture.getWidth());
                return;
            }

            if (texture.getHeight() != this.fboHeight_) {
                console.log("FBO texture size mismatch: fbo height:" + this.fboHeight_ + " : texture height: " + texture.getHeight());
                return;
            }
        }
        let attachIndex = this.attachedTextures_.length;
        this.attachedTextures_.push(texture);
        let textureAttachment = gl.COLOR_ATTACHMENT0 + attachIndex;

        // attach to FBO
        this.bind();
        gl.framebufferTexture2D(gl.FRAMEBUFFER, textureAttachment, gl.TEXTURE_2D, texture.getTextureHandle(), 0);
        this.unbind();
    }

    bind() {
        this.getContext().gl().bindFramebuffer(this.getContext().gl().FRAMEBUFFER, this.fboHandle_);
    }

    unbind() {
        this.getContext().gl().bindFramebuffer(this.getContext().gl().FRAMEBUFFER, null);
    }

    createDepthStencilBuffer(width, height) {
        const gl = this.getContext().gl();
        this.bind();

        if (this.renderBufferHandle_ != null) {
            gl.deleteRenderbuffer(this.renderBufferHandle_);
        }

        this.renderBufferHandle_ = gl.createRenderbuffer();
        gl.bindRenderbuffer(gl.RENDERBUFFER, this.renderBufferHandle_);
        gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, width, height);
        gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.renderBufferHandle_);
        this.unbind();
        this.hasDepthStencil_ = true;
    }

    createShadowMappingFbo(shadowMapWidth, shadowMapHeight) {

        if (this.attachedTextures_.length != 0) {
            console.log("Error! when using fbo for shadow ampping only the shadow map can be attached");
            return;
        }

        const gl = this.getContext().gl();
        this.bind();

        this.fboWidth_ = shadowMapWidth;
        this.fboHeight_ = shadowMapHeight;

        // create the depth texture
        var shadowMapDepthTexture = new wgTexture("shadow map depth texture", this.getContext());
        shadowMapDepthTexture.createShadowMapDepthTexture(this.fboWidth_, this.fboHeight_);
        this.attachedTextures_.push(shadowMapDepthTexture);

        // attach to FBO
        this.bind();
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, shadowMapDepthTexture.getTextureHandle(), 0);
        gl.drawBuffers([gl.NONE]);
        gl.readBuffer(gl.NONE);
        this.unbind();

        shadowMapDepthTexture.unbind();

        this.hasDepthStencil_ = true;
        this.hasFixedSize_ = true;
    }

    createCascadingShadowMappingFbo(shadowMapWidth, shadowMapHeight, numberOfCascades) {

        if (this.attachedTextures_.length != 0) {
            console.log("Error! when using fbo for shadow ampping only the shadow map can be attached");
            return;
        }

        const gl = this.getContext().gl();
        this.bind();

        this.fboWidth_ = shadowMapWidth;
        this.fboHeight_ = shadowMapHeight;

        // create the depth textures
        for (let i = 0; i < numberOfCascades; i++) {
            var shadowMapDepthTexture = new wgTexture("shadowMap" + i, this.getContext());
            shadowMapDepthTexture.createShadowMapDepthTexture(this.fboWidth_, this.fboHeight_);
            shadowMapDepthTexture.setActiveTexture(gl.TEXTURE0 + i);
            this.attachedTextures_.push(shadowMapDepthTexture);
            shadowMapDepthTexture.unbind();
        }

        this.hasDepthStencil_ = true;
        this.hasFixedSize_ = true;
    }

    // asumes that the shadow texture has been created with createCascadingShadowMappingFbo
    attachShadowMap(index){
        if(index < 0 || index >= this.attachedTextures_.length) return;

        const gl = this.getContext().gl();
        this.bind();
        gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, this.attachedTextures_[index].getTextureHandle(), 0);
        gl.drawBuffers([gl.NONE]);
        gl.readBuffer(gl.NONE);
        this.unbind();
    }

    getAttachment(index) {
        if (index >= this.attachedTextures_.length) {
            return null;
        }
        return this.attachedTextures_[index];
    }

    resize(width, height) {
        if (this.hasFixedSize_) return;

        let resizeTextures = false;

        if (this.fboWidth_ != width) {
            this.fboWidth_ = width;
            resizeTextures = true;
        }

        if (this.fboHeight_ != height) {
            this.fboHeight_ = height;
            resizeTextures = true;
        }

        if (resizeTextures) {
            const gl = this.getContext().gl();

            for (let i = 0; i < this.attachedTextures_.length; i++) {
                this.attachedTextures_[i].resize2D(width, height);
                const textureAttachment = gl.COLOR_ATTACHMENT0 + i;

                // re attach
                this.bind();
                gl.framebufferTexture2D(gl.FRAMEBUFFER, textureAttachment, gl.TEXTURE_2D, this.attachedTextures_[i].getTextureHandle(), 0);
                this.unbind();
            }

            if (this.hasDepthStencil_) {
                if (this.renderBufferHandle_ != null) {
                    gl.deleteRenderbuffer(this.renderBufferHandle_);
                    this.renderBufferHandle_ = null;
                }
                this.createDepthStencilBuffer(width, height);
            }
        }
    }
}

export { wgFbo };
